import { useLog } from '@/features/shared/Hooks/useLog';
import { isUrlInDomainList } from '@/hooks/useRequiredInterceptor';
import { StatusCodes } from '@/utils/constants/http-codes';
import axios, { AxiosError, AxiosPromise } from 'axios';
import React from 'react';

export const useAuthErrorInterceptor = ({
  isAuthorized,
  onAuthRequired,
  onAuthError,
  trustedDomains,
  excludedDomainsForErrors = [],
}: {
  isAuthorized: boolean | null;
  onAuthRequired: () => void;
  onAuthError?: (error: AxiosError) => AxiosPromise<any>;
  trustedDomains: string[];
  excludedDomainsForErrors?: string[];
}) => {
  const isAuthorizedRef = React.useRef<boolean | null>(isAuthorized);
  const waitingForAuthorization = React.useRef<(() => void)[]>([]);
  const log = useLog({ context: 'useAuthErrorInterceptor' });

  React.useEffect(() => {
    if (isAuthorized !== null) {
      // Resolve pending requests if isReady is true
      isAuthorizedRef.current = isAuthorized;
      waitingForAuthorization.current.forEach((resolve) => resolve());
      waitingForAuthorization.current = [];
    }
  }, [isAuthorized]);

  // Handle 401 Error
  const handle401Error = React.useCallback(
    async (error: AxiosError) => {
      const onError = () => (onAuthError ? onAuthError(error) : Promise.reject(error));

      // If Unauthorized, reject
      if (isAuthorizedRef.current === false) return onError();

      isAuthorizedRef.current = null;

      // Wait for Authorization
      await new Promise<void>((resolve) => {
        waitingForAuthorization.current.push(resolve);

        // Authorization required
        onAuthRequired();
      });

      // If Unauthorized, reject
      if (isAuthorizedRef.current === false) return onError();

      await new Promise((resolve) => setTimeout(resolve));

      // If Authorized, retry previous request
      return axios(error.config);
    },
    [onAuthError, onAuthRequired],
  );

  // Response Interceptor
  React.useEffect(() => {
    const ejectId = axios.interceptors.response.use(
      (config) => config,
      async (error: AxiosError) => {
        const { status } = error?.response || {};

        const bypassErrorFlow = isUrlInDomainList(error.config?.url, excludedDomainsForErrors);
        const isNotTrustedDomain = !isUrlInDomainList(error.config?.url, trustedDomains);

        if (bypassErrorFlow) return Promise.reject(error);
        if (isNotTrustedDomain) return Promise.reject(error);

        switch (status) {
          case StatusCodes.UNAUTHORIZED:
            return handle401Error(error);
          default:
            return onAuthError ? onAuthError(error) : Promise.reject(error);
        }
      },
    );

    return () => {
      axios.interceptors.response.eject(ejectId);
    };
  }, [handle401Error, onAuthRequired, trustedDomains, onAuthError, excludedDomainsForErrors, log]);
};
